import V from "@/admin/vm/VmImages.module.css";
import { vmImageOrderMutation, type VmImageType } from "@/admin/vm/vmImagesQuery.ts";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { OsImageIcon } from "@/modules/compute/os/OsImageIcon.tsx";
import { useListData } from "@react-stately/data";
import type { VmImage, VmImageOrderResponse } from "@warrenio/api-spec/spec.oats.gen";
import { ListBox, ListBoxItem, useDragAndDrop } from "react-aria-components";

export function OrderVmImageButton({ image_type, items }: { image_type: VmImageType; items: VmImage[] }) {
    return (
        <WModal button={<WModalButton label="Order" color="primary" size="xs" variant="basic" />}>
            <OrderVmImageModalContent image_type={image_type} items={items} />
        </WModal>
    );
}

function OrderVmImageModalContent({ image_type, items }: { image_type: VmImageType; items: VmImage[] }) {
    const updateMutation = useStandardMutation(vmImageOrderMutation);

    const initialItems = items.map((obj) => {
        return {
            id: obj.ui_position,
            name: obj.os_name,
            title: obj.display_name,
        };
    });

    const list = useListData({
        initialItems: initialItems,
    });

    const { dragAndDropHooks } = useDragAndDrop({
        getItems: (keys) => [...keys].map((key) => ({ "text/plain": list.getItem(key)?.name ?? "" })),
        onReorder(e) {
            if (e.target.dropPosition === "before") {
                list.moveBefore(e.target.key, e.keys);
            } else if (e.target.dropPosition === "after") {
                list.moveAfter(e.target.key, e.keys);
            }
        },
    });

    async function onSubmit() {
        const body: VmImageOrderResponse = list.items.map((obj, index) => {
            return {
                ui_position: index + 1,
                os_name: obj.name,
            };
        });

        await updateMutation.mutateAsync({
            body,
            image_type,
        });
    }

    return (
        <WModalContent title="Order VM images" label="Save" modalAction={async () => await onSubmit()}>
            <ListBox
                className={V.DragList}
                items={list.items}
                aria-label="VM images"
                selectionMode="single"
                dragAndDropHooks={dragAndDropHooks}
            >
                {(item) => (
                    <ListBoxItem className={V.DragListItem} textValue={item.name}>
                        <MaskIcon className="jp-icon-menu color-muted size-0.875rem" />
                        <OsImageIcon os_name={item.name} className="color-grey-5 size-0.875rem" />
                        {item.title}
                    </ListBoxItem>
                )}
            </ListBox>
        </WModalContent>
    );
}
